import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Send, Plus, Trash2, Upload } from 'lucide-react';

const WorkOrderForm = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [step, setStep] = useState(1); // Multi-step form
  const [formData, setFormData] = useState({
    // Step 1 - Basic Info
    billing_code: 'BWO',
    customer_id: '',
    property_type: '',
    location_id: '',
    
    // Step 2 - Work Order Details
    date_requested: new Date().toISOString().split('T')[0],
    requested_by: '',
    category: '',
    description_of_problem: '',
    urgency_level: 'Medium',
    
    // Step 3 - Work Performed (optional for creation)
    description_of_work_performed: '',
    start_time: '',
    end_time: '',
    travel_time: '',
    
    // Step 4 - Materials
    materials_used: false,
    materials: [],
    
    // Step 5 - Photos & Signatures
    before_photos: [],
    after_photos: [],
    technician_signature: '',
    customer_signature: '',
    customer_not_present: false,
    
    status: 'Open'
  });
  
  const [currentMaterial, setCurrentMaterial] = useState({
    description: '',
    quantity: '',
    receipt_photo: ''
  });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (formData.customer_id) {
      fetchLocations(formData.customer_id);
    }
  }, [formData.customer_id]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchLocations = async (customerId) => {
    try {
      const response = await axios.get(`${API}/service-locations/by-customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
      setLocations([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      // Use default categories if none found
      setCategories([
        { id: '1', name: 'Plumbing', name_es: 'Plomería' },
        { id: '2', name: 'Electrical', name_es: 'Eléctrico' },
        { id: '3', name: 'HVAC', name_es: 'HVAC' },
        { id: '4', name: 'Cleaning', name_es: 'Limpieza' },
        { id: '5', name: 'General Maintenance', name_es: 'Mantenimiento General' }
      ]);
    }
  };

  const calculateLaborHours = () => {
    if (formData.start_time && formData.end_time) {
      const start = new Date(`2000-01-01T${formData.start_time}`);
      const end = new Date(`2000-01-01T${formData.end_time}`);
      const diff = (end - start) / (1000 * 60 * 60);
      return diff > 0 ? diff.toFixed(2) : 0;
    }
    return 0;
  };

  const addMaterial = () => {
    if (currentMaterial.description && currentMaterial.quantity) {
      setFormData({
        ...formData,
        materials: [...formData.materials, currentMaterial]
      });
      setCurrentMaterial({ description: '', quantity: '', receipt_photo: '' });
      toast.success('Material added');
    }
  };

  const removeMaterial = (index) => {
    const newMaterials = formData.materials.filter((_, i) => i !== index);
    setFormData({ ...formData, materials: newMaterials });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create the work order
      const woData = {
        billing_code: formData.billing_code,
        customer_id: formData.customer_id,
        location_id: formData.location_id,
        property_type: formData.property_type,
        date_requested: formData.date_requested,
        requested_by: formData.requested_by,
        category: formData.category,
        description_of_problem: formData.description_of_problem,
        urgency_level: formData.urgency_level
      };

      const createResponse = await axios.post(`${API}/work-orders`, woData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // If work was performed, update the work order
      if (formData.description_of_work_performed) {
        const updateData = {
          description_of_work_performed: formData.description_of_work_performed,
          start_time: formData.start_time,
          end_time: formData.end_time,
          total_labor_hours: parseFloat(calculateLaborHours()),
          travel_time: formData.travel_time ? parseFloat(formData.travel_time) : null,
          status: formData.status,
          materials_used: formData.materials_used,
          materials: formData.materials,
          before_photos: formData.before_photos,
          after_photos: formData.after_photos,
          technician_signature: formData.technician_signature,
          customer_signature: formData.customer_signature,
          customer_not_present: formData.customer_not_present
        };

        await axios.put(`${API}/work-orders/${createResponse.data.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Work Order created successfully!');
      navigate('/field/work-orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create work order');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    // Validation for each step
    if (step === 1 && (!formData.customer_id || !formData.location_id || !formData.property_type)) {
      toast.error('Please complete all required fields');
      return;
    }
    if (step === 2 && (!formData.requested_by || !formData.category || !formData.description_of_problem)) {
      toast.error('Please complete all required fields');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div>
      <Button
        variant="outline"
        onClick={() => navigate('/field')}
        className="mb-4 bg-white"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back / Volver
      </Button>

      <Card className="shadow-xl">
        <div className="h-4" style={{ backgroundColor: '#C19A6B' }} />
        <CardHeader>
          <CardTitle className="text-2xl">
            Work Order / Orden de Trabajo
          </CardTitle>
          <CardDescription>
            Step {step} of 5 / Paso {step} de 5
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1 - Location Selection */}
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Location Information / Información de Ubicación</h3>
                
                <div className="space-y-2">
                  <Label>Billing Type / Tipo de Facturación *</Label>
                  <RadioGroup
                    value={formData.billing_code}
                    onValueChange={(value) => setFormData({ ...formData, billing_code: value })}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="BWO" id="bwo" />
                      <Label htmlFor="bwo" className="font-normal">
                        <span className="font-mono text-xs bg-amber-100 px-2 py-1 rounded">bwo</span> Billable Work Order / Orden de Trabajo Facturable
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="NB" id="nb" />
                      <Label htmlFor="nb" className="font-normal">
                        <span className="font-mono text-xs bg-green-100 px-2 py-1 rounded">nb</span> Non-Billable / No Facturable
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customer">Customer / Cliente *</Label>
                  <select
                    id="customer"
                    data-testid="wo-customer-select"
                    className="w-full border rounded-md px-3 py-2"
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value, location_id: '' })}
                    required
                  >
                    <option value="">Select Customer / Seleccionar Cliente</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                </div>

                {formData.customer_id && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="property_type">Property Type / Tipo de Propiedad *</Label>
                      <select
                        id="property_type"
                        data-testid="wo-property-type-select"
                        className="w-full border rounded-md px-3 py-2"
                        value={formData.property_type}
                        onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                        required
                      >
                        <option value="">Select Type / Seleccionar Tipo</option>
                        <option value="Bank">Bank / Banco</option>
                        <option value="Commercial">Commercial / Comercial</option>
                        <option value="School">School / Escuela</option>
                        <option value="Retail">Retail / Minorista</option>
                        <option value="Mixed-Use">Mixed-Use / Uso Mixto</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="location">Location / Ubicación *</Label>
                      <select
                        id="location"
                        data-testid="wo-location-select"
                        className="w-full border rounded-md px-3 py-2"
                        value={formData.location_id}
                        onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                        required
                      >
                        <option value="">Select Location / Seleccionar Ubicación</option>
                        {locations.map(location => (
                          <option key={location.id} value={location.id}>
                            {location.name} ({location.type})
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <Button
                  type="button"
                  onClick={nextStep}
                  className="w-full"
                  style={{ backgroundColor: '#6B9F3E' }}
                  data-testid="next-step-btn"
                >
                  Next / Siguiente
                </Button>
              </div>
            )}

            {/* Step 2 - Work Order Details */}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Work Order Details / Detalles de la Orden</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_requested">Date Requested / Fecha Solicitada *</Label>
                    <Input
                      id="date_requested"
                      type="date"
                      value={formData.date_requested}
                      onChange={(e) => setFormData({ ...formData, date_requested: e.target.value })}
                      required
                      data-testid="wo-date-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="requested_by">Requested By / Solicitado Por *</Label>
                    <Input
                      id="requested_by"
                      value={formData.requested_by}
                      onChange={(e) => setFormData({ ...formData, requested_by: e.target.value })}
                      required
                      data-testid="wo-requested-by-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category / Categoría *</Label>
                  <select
                    id="category"
                    data-testid="wo-category-select"
                    className="w-full border rounded-md px-3 py-2"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    <option value="">Select Category / Seleccionar Categoría</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name} / {cat.name_es}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="urgency">Urgency Level / Nivel de Urgencia *</Label>
                  <select
                    id="urgency"
                    data-testid="wo-urgency-select"
                    className="w-full border rounded-md px-3 py-2"
                    value={formData.urgency_level}
                    onChange={(e) => setFormData({ ...formData, urgency_level: e.target.value })}
                    required
                  >
                    <option value="Low">Low / Bajo</option>
                    <option value="Medium">Medium / Medio</option>
                    <option value="High">High / Alto</option>
                    <option value="Emergency">Emergency / Emergencia</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="problem">Description of Problem / Descripción del Problema *</Label>
                  <Textarea
                    id="problem"
                    value={formData.description_of_problem}
                    onChange={(e) => setFormData({ ...formData, description_of_problem: e.target.value })}
                    rows={4}
                    required
                    data-testid="wo-problem-input"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="flex-1"
                  >
                    Back / Atrás
                  </Button>
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="flex-1"
                    style={{ backgroundColor: '#6B9F3E' }}
                  >
                    Next / Siguiente
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3 - Work Performed (Optional) */}
            {step === 3 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Work Performed / Trabajo Realizado (Optional)</h3>
                <p className="text-sm text-slate-600">You can skip this and complete it later / Puede omitir esto y completarlo más tarde</p>

                <div className="space-y-2">
                  <Label htmlFor="work_performed">Description of Work Performed</Label>
                  <Textarea
                    id="work_performed"
                    value={formData.description_of_work_performed}
                    onChange={(e) => setFormData({ ...formData, description_of_work_performed: e.target.value })}
                    rows={4}
                    data-testid="wo-work-performed-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_time">Start Time / Hora de Inicio</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      data-testid="wo-start-time-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_time">End Time / Hora de Finalización</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      data-testid="wo-end-time-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Labor Hours / Horas de Trabajo</Label>
                    <Input
                      value={calculateLaborHours()}
                      disabled
                      className="bg-slate-100"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="travel_time">Travel Time (hours) / Tiempo de Viaje (horas)</Label>
                  <Input
                    id="travel_time"
                    type="number"
                    step="0.1"
                    value={formData.travel_time}
                    onChange={(e) => setFormData({ ...formData, travel_time: e.target.value })}
                    data-testid="wo-travel-time-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status / Estado</Label>
                  <select
                    id="status"
                    className="w-full border rounded-md px-3 py-2"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    data-testid="wo-status-select"
                  >
                    <option value="Open">Open / Abierto</option>
                    <option value="In Progress">In Progress / En Progreso</option>
                    <option value="Pending">Pending / Pendiente</option>
                    <option value="Completed">Completed / Completado</option>
                    {formData.billing_code === 'BWO' && <option value="Pending Pricing">Pending Pricing / Pendiente de Precio</option>}
                  </select>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="flex-1"
                  >
                    Back / Atrás
                  </Button>
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="flex-1"
                    style={{ backgroundColor: '#6B9F3E' }}
                  >
                    Next / Siguiente
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4 - Materials */}
            {step === 4 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Materials / Materiales</h3>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="materials_used"
                    checked={formData.materials_used}
                    onCheckedChange={(checked) => setFormData({ ...formData, materials_used: checked })}
                    data-testid="wo-materials-used-checkbox"
                  />
                  <Label htmlFor="materials_used" className="font-normal">
                    Were materials used? / ¿Se utilizaron materiales?
                  </Label>
                </div>

                {formData.materials_used && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <div className="space-y-3">
                      <Input
                        placeholder="Material Description / Descripción del Material"
                        value={currentMaterial.description}
                        onChange={(e) => setCurrentMaterial({ ...currentMaterial, description: e.target.value })}
                        data-testid="wo-material-description-input"
                      />
                      <Input
                        placeholder="Quantity / Cantidad"
                        value={currentMaterial.quantity}
                        onChange={(e) => setCurrentMaterial({ ...currentMaterial, quantity: e.target.value })}
                        data-testid="wo-material-quantity-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addMaterial}
                        className="w-full"
                        data-testid="wo-add-material-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Material / Agregar Material
                      </Button>
                    </div>

                    {formData.materials.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Materials Added:</Label>
                        {formData.materials.map((material, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                            <div>
                              <p className="font-medium">{material.description}</p>
                              <p className="text-sm text-slate-600">Qty: {material.quantity}</p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeMaterial(index)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="flex-1"
                  >
                    Back / Atrás
                  </Button>
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="flex-1"
                    style={{ backgroundColor: '#6B9F3E' }}
                  >
                    Next / Siguiente
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5 - Signatures */}
            {step === 5 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Signatures / Firmas</h3>

                <div className="space-y-2">
                  <Label htmlFor="tech_signature">
                    Technician Signature (Full Name) / Firma del Técnico (Nombre Completo) *
                  </Label>
                  <Input
                    id="tech_signature"
                    value={formData.technician_signature}
                    onChange={(e) => setFormData({ ...formData, technician_signature: e.target.value })}
                    placeholder="Type your full name / Escriba su nombre completo"
                    required
                    data-testid="wo-tech-signature-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customer_signature">
                    Customer Signature (Optional) / Firma del Cliente (Opcional)
                  </Label>
                  <Input
                    id="customer_signature"
                    value={formData.customer_signature}
                    onChange={(e) => setFormData({ ...formData, customer_signature: e.target.value })}
                    placeholder="Customer name / Nombre del cliente"
                    data-testid="wo-customer-signature-input"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="customer_not_present"
                    checked={formData.customer_not_present}
                    onCheckedChange={(checked) => setFormData({ ...formData, customer_not_present: checked })}
                    data-testid="wo-customer-not-present-checkbox"
                  />
                  <Label htmlFor="customer_not_present" className="font-normal">
                    Customer Not Present / Cliente No Presente
                  </Label>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="flex-1"
                  >
                    Back / Atrás
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={loading}
                    data-testid="wo-submit-btn"
                    style={{ backgroundColor: '#6B9F3E' }}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {loading ? 'Creating... / Creando...' : 'Create Work Order / Crear Orden de Trabajo'}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkOrderForm;
